import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  Tag,
  Percent,
  DollarSign,
  Clock,
  ShoppingBag,
  Edit2,
  Trash2,
  Copy,
  MoreVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  X,
  ChevronDown,
  Calendar,
  Timer,
  Repeat,
  Users,
  Layers,
  Target,
  Gift,
  Package,
  TrendingUp,
  Building2,
} from "lucide-react";

import toast from "react-hot-toast";
import DiscountAddEditForm from "./DiscountAddEditForm";
import DiscountViewModal from "./DiscountViewModal";
import { ConfirmDialog } from "../../Menu_Management/UIComponents";
import Pagination from "@/components/shared/Pagination";
import { useDiscount } from "@/hooks/useDiscount";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

// Discount type configurations
const DISCOUNT_TYPES = {
  PERCENTAGE: { label: "Percentage", icon: Percent, color: "blue" },
  FLAT: { label: "Flat Amount", icon: DollarSign, color: "green" },
  BOGO: { label: "Buy One Get One", icon: ShoppingBag, color: "purple" },
  BUY_X_GET_Y: { label: "Buy X Get Y", icon: ShoppingBag, color: "indigo" },
  COMPLIMENTARY: { label: "Complimentary", icon: Gift, color: "pink" },
  BUNDLE: { label: "Bundle Deal", icon: Package, color: "orange" },
  TIERED: { label: "Tiered", icon: TrendingUp, color: "indigo" },
};

const APPLICABLE_TO = {
  ALL: { label: "All Items (Bill Level)", color: "slate" },
  CATEGORY: { label: "Category", color: "amber" },
  ITEM: { label: "Specific Items", color: "cyan" },
  ORDER_TYPE: { label: "Order Type", color: "rose" },
};

const STATUS_CONFIG = {
  active: { label: "Active", color: "emerald", icon: CheckCircle },
  inactive: { label: "Inactive", color: "slate", icon: XCircle },
  scheduled: { label: "Scheduled", color: "blue", icon: Clock },
  expired: { label: "Expired", color: "red", icon: AlertCircle },
};

const getDiscountStatus = (discount) => {
  if (discount.isActive === null || discount.isActive === undefined) {
    return null;
  }

  if (!discount.isActive) return "inactive";

  const now = new Date();
  const startDate = discount.startDate ? new Date(discount.startDate) : null;
  const endDate = discount.endDate ? new Date(discount.endDate) : null;

  if (endDate && now > endDate) return "expired";
  if (startDate && now < startDate) return "scheduled";

  return "active";
};

// Parse tier slabs
const parseTierSlabs = (tierConfig) => {
  if (!tierConfig) return [];
  try {
    const parsed = typeof tierConfig === "string" ? JSON.parse(tierConfig) : tierConfig;
    return Array.isArray(parsed) ? parsed.sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)) : [];
  } catch { return []; }
};

// Format discount value display
const formatDiscountValue = (discount) => {
  if (discount.type === "PERCENTAGE") {
    return `${discount.value}%`;
  } else if (discount.type === "FLAT") {
    return `₹${discount.value}`;
  } else if (discount.type === "BOGO") {
    return "Buy 1 Get 1";
  } else if (discount.type === "BUY_X_GET_Y") {
    return `Buy ${discount.buyQuantity} Get ${discount.getQuantity}`;
  } else if (discount.type === "COMPLIMENTARY") {
    return `Buy ${discount.buyQuantity || 1} Get ${discount.getQuantity || 1} Free`;
  } else if (discount.type === "BUNDLE") {
    return `Combo @ ₹${discount.bundlePrice || 0}`;
  } else if (discount.type === "TIERED") {
    const slabs = parseTierSlabs(discount.tierConfig);
    if (slabs.length > 0) {
      return slabs.map((s) => `${s.value}%`).join(" / ");
    }
    return "Tiered";
  }
  return discount.value;
};

export default function DiscountList({ renderActions, selectedBranchId, isOwner }) {
  const { hasPermission } = useAuth();

  // Permission flags
  const canView      = hasPermission(P.CRM.DISCOUNTS.SCREEN);
  const canAdd       = hasPermission(P.CRM.DISCOUNTS.ADD);
  const canEdit      = hasPermission(P.CRM.DISCOUNTS.EDIT);
  const canDuplicate = hasPermission(P.CRM.DISCOUNTS.DUPLICATE);
  const canToggle    = hasPermission(P.CRM.DISCOUNTS.TOGGLE);
  const canDelete    = hasPermission(P.CRM.DISCOUNTS.DELETE);

  // Show actions column only if user has at least one row-level action
  const hasAnyRowAction = canEdit || canDuplicate || canToggle || canDelete;

  const {
    discounts,
    loading,
    fetchDiscounts,
    createDiscount,
    updateDiscount,
    deleteDiscount,
    deactivateDiscount,
    getDiscountRawById,
    setDiscounts,
  } = useDiscount();

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);

  // Advanced filters
  const [filters, setFilters] = useState({
    discountType: "",
    appliesTo: "",
    orderType: "",
    hasDateRestriction: "",
    hasTimeRestriction: "",
    hasDayRestriction: "",
    hasUsageLimit: "",
    minBillRange: "",
    specificItem: "",
    specificCategory: "",
  });

  const FILTER_OPTIONS = {
    discountType: [
      { value: "", label: "All Types" },
      { value: "PERCENTAGE", label: "Percentage" },
      { value: "FLAT", label: "Fixed Amount" },
      { value: "BOGO", label: "BOGO" },
      { value: "BUY_X_GET_Y", label: "Buy X Get Y" },
      { value: "COMPLIMENTARY", label: "Complimentary" },
      { value: "BUNDLE", label: "Bundle Deal" },
      { value: "TIERED", label: "Tiered" },
    ],
    appliesTo: [
      { value: "", label: "All" },
      { value: "ALL", label: "Bill Level" },
      { value: "ITEM", label: "Specific Items" },
      { value: "CATEGORY", label: "Category" },
    ],
    orderType: [
      { value: "", label: "All Order Types" },
      { value: "dine_in", label: "Dine-In" },
      { value: "takeaway", label: "Takeaway" },
      { value: "delivery", label: "Delivery" },
    ],
    hasDateRestriction: [
      { value: "", label: "Any" },
      { value: "yes", label: "Has Date Limit" },
      { value: "no", label: "No Date Limit" },
    ],
    hasTimeRestriction: [
      { value: "", label: "Any" },
      { value: "yes", label: "Has Time Window" },
      { value: "no", label: "All Day" },
    ],
    hasDayRestriction: [
      { value: "", label: "Any" },
      { value: "yes", label: "Specific Days" },
      { value: "no", label: "All Days" },
    ],
    hasUsageLimit: [
      { value: "", label: "Any" },
      { value: "yes", label: "Limited Usage" },
      { value: "no", label: "Unlimited" },
    ],
    minBillRange: [
      { value: "", label: "Any Amount" },
      { value: "none", label: "No Minimum" },
      { value: "low", label: "₹1 - ₹500" },
      { value: "medium", label: "₹501 - ₹1000" },
      { value: "high", label: "Above ₹1000" },
    ],
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const resetFilters = () => {
    setFilters({
      discountType: "",
      appliesTo: "",
      orderType: "",
      hasDateRestriction: "",
      hasTimeRestriction: "",
      hasDayRestriction: "",
      hasUsageLimit: "",
      minBillRange: "",
      specificItem: "",
      specificCategory: "",
    });
  };

  const { uniqueItems, uniqueCategories } = useMemo(() => {
    const itemsMap = new Map();
    const categoriesMap = new Map();

    discounts.forEach((discount) => {
      if (discount.menuItems && discount.menuItems.length > 0) {
        discount.menuItems.forEach((item) => {
          if (item.id && item.name && !itemsMap.has(item.id)) {
            itemsMap.set(item.id, { id: item.id, name: item.name });
          }
        });
      }
      if (discount.categories && discount.categories.length > 0) {
        discount.categories.forEach((cat) => {
          if (cat.id && cat.name && !categoriesMap.has(cat.id)) {
            categoriesMap.set(cat.id, { id: cat.id, name: cat.name });
          }
        });
      }
    });

    const items = Array.from(itemsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    const categories = Array.from(categoriesMap.values()).sort((a, b) => a.name.localeCompare(b.name));

    return { uniqueItems: items, uniqueCategories: categories };
  }, [discounts]);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "",
    data: null,
  });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchDiscounts(selectedBranchId ? { branchId: selectedBranchId } : {});
  }, [selectedBranchId, fetchDiscounts]);

  // Expose actions to parent.
  // Re-runs whenever canAdd changes (e.g. after refreshPermissions) so the
  // parent Add button appears/disappears immediately without a page reload.
  useEffect(() => {
    if (renderActions) {
      renderActions({
        // Pass onAdd only when the user actually has the ADD permission.
        // Explicitly pass onAdd: null when canAdd is false so the parent
        // removes the button rather than keeping a stale one from a previous render.
        onAdd: canAdd
          ? () => {
              setShowForm(true);
              setEditItem(null);
            }
          : null,
        onRefresh: () => {
          fetchDiscounts(selectedBranchId ? { branchId: selectedBranchId } : {});
          showToast("Discounts refreshed", "success");
        },
      });
    }
  }, [renderActions, fetchDiscounts, selectedBranchId, canAdd]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest(".action-menu-container")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const filteredDiscounts = useMemo(() => {
    return discounts.filter((discount) => {
      const status = getDiscountStatus(discount);

      if (activeTab !== "all" && status !== activeTab) return false;

      if (filterText) {
        const text = filterText.toLowerCase();
        const matchesText =
          discount.name?.toLowerCase().includes(text) ||
          discount.code?.toLowerCase().includes(text) ||
          (DISCOUNT_TYPES[discount.type || "PERCENTAGE"]?.label || "").toLowerCase().includes(text);
        if (!matchesText) return false;
      }

      if (filters.discountType && discount.type !== filters.discountType) return false;
      if (filters.appliesTo && discount.applicableTo !== filters.appliesTo) return false;

      if (filters.orderType) {
        const discountOrderTypes = discount.orderTypes || [];
        if (discountOrderTypes.length > 0 && !discountOrderTypes.includes(filters.orderType)) return false;
      }

      if (filters.hasDateRestriction) {
        const hasDateLimit = discount.startDate || discount.endDate;
        if (filters.hasDateRestriction === "yes" && !hasDateLimit) return false;
        if (filters.hasDateRestriction === "no" && hasDateLimit) return false;
      }

      if (filters.hasTimeRestriction) {
        const hasTimeLimit = discount.startTime && discount.endTime;
        if (filters.hasTimeRestriction === "yes" && !hasTimeLimit) return false;
        if (filters.hasTimeRestriction === "no" && hasTimeLimit) return false;
      }

      if (filters.hasDayRestriction) {
        const daysOfWeek = discount.daysOfWeek || [];
        const hasDayLimit = daysOfWeek.length > 0 && daysOfWeek.length < 7;
        if (filters.hasDayRestriction === "yes" && !hasDayLimit) return false;
        if (filters.hasDayRestriction === "no" && hasDayLimit) return false;
      }

      if (filters.hasUsageLimit) {
        const hasLimit = discount.usageLimit != null && discount.usageLimit > 0;
        if (filters.hasUsageLimit === "yes" && !hasLimit) return false;
        if (filters.hasUsageLimit === "no" && hasLimit) return false;
      }

      if (filters.minBillRange) {
        const minAmount = discount.minOrderAmount || 0;
        switch (filters.minBillRange) {
          case "none": if (minAmount > 0) return false; break;
          case "low": if (minAmount < 1 || minAmount > 500) return false; break;
          case "medium": if (minAmount < 501 || minAmount > 1000) return false; break;
          case "high": if (minAmount <= 1000) return false; break;
        }
      }

      if (filters.specificItem) {
        const itemId = parseInt(filters.specificItem, 10);
        const discountItemIds = discount.menuItems?.map((i) => i.id) || discount.applicableItems || [];
        if (!discountItemIds.includes(itemId)) return false;
      }

      if (filters.specificCategory) {
        const categoryId = parseInt(filters.specificCategory, 10);
        const discountCategoryIds = discount.categories?.map((c) => c.id) || discount.applicableCategories || [];
        if (!discountCategoryIds.includes(categoryId)) return false;
      }

      return true;
    });
  }, [discounts, activeTab, filterText, filters]);

  const sortedDiscounts = useMemo(() => {
    const sortable = [...filteredDiscounts];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (aVal == null) aVal = "";
        if (bVal == null) bVal = "";

        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredDiscounts, sortConfig]);

  const statusCounts = useMemo(() => {
    const counts = { all: discounts.length, active: 0, inactive: 0, scheduled: 0, expired: 0 };
    discounts.forEach((d) => {
      const status = getDiscountStatus(d);
      if (status && counts[status] != null) counts[status]++;
    });
    return counts;
  }, [discounts]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortConfig.direction === "asc"
      ? <ArrowUp size={14} className="text-gray-700" />
      : <ArrowDown size={14} className="text-gray-700" />;
  };

  const handleSave = async (apiPayload) => {
    if (editItem && editItem.id) {
      const updated = await updateDiscount(editItem.id, apiPayload);
      return updated;
    } else {
      const created = await createDiscount(apiPayload);
      return created;
    }
  };

  const handleDeleteClick = (id) => {
    if (!canDelete) {
      toast.error("You don't have permission to delete discounts");
      return;
    }
    setConfirmDialog({ isOpen: true, type: "delete", data: id });
  };

  const confirmDelete = async () => {
    const id = confirmDialog.data;
    try {
      await deleteDiscount(id);
      await fetchDiscounts();
      showToast("Discount deleted", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete discount", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: "", data: null });
    }
  };

  const handleDeactivate = async (discount) => {
    if (!canToggle) {
      toast.error("You don't have permission to deactivate discounts");
      setOpenActionMenu(null);
      return;
    }
    try {
      await deactivateDiscount(discount.id);
      showToast("Discount deactivated", "success");
    } catch (err) {
      showToast("Failed to deactivate discount", "error");
    } finally {
      setOpenActionMenu(null);
    }
  };

  const handleDuplicate = async (discount) => {
    if (!canDuplicate) {
      toast.error("You don't have permission to duplicate discounts");
      setOpenActionMenu(null);
      return;
    }
    setOpenActionMenu(null);
    try {
      const original = await getDiscountRawById(discount.id);
      if (!original) throw new Error("Original discount not found");

      const payload = {
        code: original.code ? `${original.code}_COPY` : null,
        name: original.name ? `${original.name} (Copy)` : "Discount (Copy)",
        description: original.description ?? "",
        discountType: original.discountType ?? "percentage",
        discountValue: original.discountValue ?? 0,
        appliesTo: original.appliesTo ?? "bill",
        idMenuItems: original.idMenuItems ?? null,
        idCategories: original.idCategories ?? null,
        validFrom: original.validFrom ?? null,
        validUntil: original.validUntil ?? null,
        startTime: original.startTime ?? null,
        endTime: original.endTime ?? null,
        daysOfWeek: Array.isArray(original.daysOfWeek) ? original.daysOfWeek : [],
        orderTypes: Array.isArray(original.orderTypes) ? original.orderTypes : [],
        minBillAmount: original.minBillAmount ?? null,
        maxDiscountAmount: original.maxDiscountAmount ?? null,
        usageLimit: original.usageLimit ?? null,
        usagePerCustomer: original.usagePerCustomer ?? null,
        buyQuantity: original.buyQuantity ?? null,
        getQuantity: original.getQuantity ?? null,
      };

      await createDiscount(payload);
      showToast("Discount duplicated", "success");
    } catch (err) {
      console.error("Duplicate discount failed", err);
      showToast("Failed to duplicate discount", "error");
    }
  };

  const tabs = [
    { id: "all", label: "All", count: statusCounts.all },
    { id: "active", label: "Active", count: statusCounts.active },
    { id: "scheduled", label: "Scheduled", count: statusCounts.scheduled },
    { id: "expired", label: "Expired", count: statusCounts.expired },
    { id: "inactive", label: "Inactive", count: statusCounts.inactive },
  ];

  const totalItems = sortedDiscounts.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, activeTab, pageSize, filters]);

  const paginatedDiscounts = useMemo(() => {
    if (loading) return [];
    const startIndex = (currentPage - 1) * pageSize;
    return sortedDiscounts.slice(startIndex, startIndex + pageSize);
  }, [sortedDiscounts, currentPage, pageSize, loading]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Compute colSpan dynamically
  // Base cols: Discount, Type & Value, Conditions, Validity, Usage, Status = 6
  // + Branch (isOwner) + Actions (hasAnyRowAction)
  const colSpan = 6 + (isOwner ? 1 : 0) + (hasAnyRowAction ? 1 : 0);

  // Access denied screen
  if (!canView) {
    return (
      <div className="min-h-full bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
          <p className="text-sm text-gray-500 mt-1">You don't have permission to view discounts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-5">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                activeTab === tab.id
                  ? "bg-teal-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search and Filter Toggle */}
        <div className="flex items-center gap-3">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, code, or type..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              showFilters || activeFilterCount > 0
                ? "bg-teal-50 border-teal-300 text-teal-700"
                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Filter size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-teal-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Discount Type", icon: Tag, key: "discountType" },
                { label: "Applies To", icon: Target, key: "appliesTo" },
                { label: "Order Type", icon: Layers, key: "orderType" },
                { label: "Min Bill Amount", icon: Target, key: "minBillRange" },
                { label: "Date Restriction", icon: Calendar, key: "hasDateRestriction" },
                { label: "Time Restriction", icon: Timer, key: "hasTimeRestriction" },
                { label: "Day Restriction", icon: Repeat, key: "hasDayRestriction" },
                { label: "Usage Limit", icon: Users, key: "hasUsageLimit" },
              ].map(({ label, icon: Icon, key }) => (
                <div key={key}>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                    <Icon size={12} />
                    {label}
                  </label>
                  <select
                    value={filters[key]}
                    onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    {FILTER_OPTIONS[key].map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {(uniqueItems.length > 0 || uniqueCategories.length > 0) && (
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                {uniqueItems.length > 0 && (
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                      <ShoppingBag size={12} />
                      Specific Menu Item
                    </label>
                    <select
                      value={filters.specificItem}
                      onChange={(e) => setFilters({ ...filters, specificItem: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                    >
                      <option value="">All Items</option>
                      {uniqueItems.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {uniqueCategories.length > 0 && (
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                      <Layers size={12} />
                      Specific Category
                    </label>
                    <select
                      value={filters.specificCategory}
                      onChange={(e) => setFilters({ ...filters, specificCategory: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th onClick={() => handleSort("name")} className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100">
                    <div className="flex items-center gap-2">Discount {getSortIcon("name")}</div>
                  </th>
                  {isOwner && (
                    <th onClick={() => handleSort("branchName")} className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-2">Branch {getSortIcon("branchName")}</div>
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Type &amp; Value</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Conditions</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Validity</th>
                  <th onClick={() => handleSort("usageCount")} className="text-center px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100">
                    <div className="flex items-center justify-center gap-2">Usage {getSortIcon("usageCount")}</div>
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Status</th>
                  {/* Actions column — only if user has at least one row action */}
                  {hasAnyRowAction && (
                    <th className="text-center px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                        <p className="text-sm font-medium">Loading discounts...</p>
                      </div>
                    </td>
                  </tr>
                ) : sortedDiscounts.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Tag className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">No discounts found</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {filterText || activeFilterCount > 0
                              ? "Try adjusting your search or filter criteria"
                              : "Get started by adding your first discount"}
                          </p>
                          {(filterText || activeFilterCount > 0) && (
                            <button
                              onClick={() => { setFilterText(""); resetFilters(); }}
                              className="mt-3 px-4 py-2 text-sm text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedDiscounts.map((discount) => {
                    const typeConfig = DISCOUNT_TYPES[discount.type] || DISCOUNT_TYPES.PERCENTAGE;
                    const TypeIcon = typeConfig.icon;

                    return (
                      <tr key={discount.id} className="hover:bg-gray-50">
                        {/* Discount Name & Code */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${typeConfig.color}-100`}>
                              <TypeIcon className={`w-5 h-5 text-${typeConfig.color}-600`} />
                            </div>
                            <div>
                              <button
                                onClick={async () => {
                                  const fullDiscount = await getDiscountRawById(discount.id);
                                  setViewItem(fullDiscount || discount);
                                }}
                                className="text-sm font-medium text-teal-600 hover:text-teal-800 hover:underline text-left"
                              >
                                {discount.name}
                              </button>
                              {discount.code && (
                                <p className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                                  {discount.code}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Branch */}
                        {isOwner && (
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <Building2 size={13} className="text-gray-400" />
                              <span className="text-sm text-gray-700">{discount.branchName || '—'}</span>
                            </div>
                          </td>
                        )}

                        {/* Type & Value */}
                        <td className="px-4 py-3">
                          <div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${typeConfig.color}-50 text-${typeConfig.color}-700`}>
                              {typeConfig.label}
                            </span>
                            <p className="text-sm font-semibold text-gray-900 mt-1">
                              {formatDiscountValue(discount)}
                              {discount.maxDiscountAmount != null && (
                                <span className="text-xs text-gray-500 font-normal ml-1">(max ₹{discount.maxDiscountAmount})</span>
                              )}
                            </p>
                          </div>
                        </td>

                        {/* Conditions */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {discount.minOrderAmount != null && (
                              <p className="text-xs text-gray-600">Min bill: <span className="font-medium">₹{discount.minOrderAmount}</span></p>
                            )}
                            <p className="text-xs text-gray-600">
                              Applies to: <span className="font-medium">{APPLICABLE_TO[discount.applicableTo] ? APPLICABLE_TO[discount.applicableTo].label : discount.applicableTo}</span>
                            </p>
                            {discount.daysOfWeek && discount.daysOfWeek.length > 0 && discount.daysOfWeek.length < 7 && (
                              <p className="text-xs text-gray-500">{discount.daysOfWeek.map((d) => d.slice(0, 3)).join(", ")}</p>
                            )}
                          </div>
                        </td>

                        {/* Validity */}
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-600">
                            {discount.startDate && <p>From: <span className="font-medium">{discount.startDate}</span></p>}
                            {discount.endDate && <p>To: <span className="font-medium">{discount.endDate}</span></p>}
                            {!discount.startDate && !discount.endDate && <p className="text-gray-400">No date limit</p>}
                            {discount.startTime && discount.endTime && (
                              <p className="text-gray-500 mt-0.5">{discount.startTime} – {discount.endTime}</p>
                            )}
                          </div>
                        </td>

                        {/* Usage */}
                        <td className="px-4 py-3 text-center">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{discount.usageCount || 0}</p>
                            {discount.usageLimit && <p className="text-xs text-gray-500">of {discount.usageLimit}</p>}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const status = getDiscountStatus(discount);
                            if (!status) return <span className="text-xs text-gray-400"></span>;
                            const statusConfig = STATUS_CONFIG[status];
                            if (!statusConfig) return <span className="text-xs text-gray-400">–</span>;
                            const StatusIcon = statusConfig.icon;
                            return (
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-50 text-${statusConfig.color}-700 border border-${statusConfig.color}-200`}>
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                            );
                          })()}
                        </td>

                        {/* Actions — only if user has at least one row action */}
                        {hasAnyRowAction && (
                          <td className="px-4 py-3 text-center">
                            <div className="inline-block action-menu-container">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openActionMenu === discount.id) {
                                    setOpenActionMenu(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setMenuPos({ top: rect.bottom + 4, left: rect.right - 176 });
                                    setOpenActionMenu(discount.id);
                                  }
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <MoreVertical size={16} className="text-gray-500" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        {!loading && sortedDiscounts.length > 0 && (
          <div className="px-2">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{sortedDiscounts.length}</span> of{" "}
              <span className="font-semibold text-gray-900">{discounts.length}</span> discounts
              {(filterText || activeFilterCount > 0) && (
                <span className="text-teal-600 ml-1">
                  (filtered{activeFilterCount > 0 ? ` by ${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""}` : ""})
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && sortedDiscounts.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl px-4 shadow-sm mt-4">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {/* Context menu (fixed position) */}
      {openActionMenu !== null && (() => {
        const discount = paginatedDiscounts.find((d) => d.id === openActionMenu);
        if (!discount) return null;
        return (
          <div
            className="fixed bg-white shadow-xl border border-gray-200 rounded-lg w-44 z-50 overflow-hidden"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {/* Edit */}
            {canEdit && (
              <button
                onClick={() => { setEditItem(discount); setShowForm(true); setOpenActionMenu(null); }}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
              >
                <Edit2 size={14} />
                Edit
              </button>
            )}

            {/* Duplicate */}
            {canDuplicate && (
              <button
                onClick={() => handleDuplicate(discount)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
              >
                <Copy size={14} />
                Duplicate
              </button>
            )}

            {/* Deactivate */}
            {canToggle && (
              <button
                onClick={() => handleDeactivate(discount)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
              >
                <XCircle size={14} />
                Deactivate
              </button>
            )}

            {/* Divider before delete */}
            {canDelete && (canEdit || canDuplicate || canToggle) && (
              <div className="border-t border-gray-100" />
            )}

            {/* Delete */}
            {canDelete && (
              <button
                onClick={() => { handleDeleteClick(discount.id); setOpenActionMenu(null); }}
                className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm text-red-600 flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        );
      })()}

      {/* Add/Edit Form Modal — only if user has ADD or EDIT permission */}
      {showForm && (canAdd || canEdit) && (
        <DiscountAddEditForm
          item={editItem}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}

      {/* Confirm Dialog for Delete */}
      {confirmDialog.isOpen && confirmDialog.type === "delete" && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, type: "", data: null })}
          title="Delete Discount"
          message="Are you sure you want to delete this discount? This action cannot be undone."
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDelete}
        />
      )}

      {/* View Modal */}
      {viewItem && (
        <DiscountViewModal
          discount={viewItem}
          onClose={() => setViewItem(null)}
        />
      )}
    </div>
  );
}